/**
 * recommendationService.js
 * Pure functions for intelligent product recommendations on PDP.
 * No side effects, no API calls — operates on in-memory product arrays.
 */

/** Bit categories eligible for similarity matching */
const BIT_CATEGORIES = [
  'drill-bits', 'reamer-bits', 'tapered-bits', 'tapered-cross-bits',
  'rh460-bits', 'rh510-bits', 'rh560-bits', 'rh570-bits',
];

/** Core fields that MUST match for a product to be a candidate */
const CORE_FIELDS = ['subcategory'];

/** Dimension fields — shared dimensions define "similar size" */
const DIMENSION_FIELDS = ['diameterMm', 'bitDiameterMm', 'thread', 'lengthMm'];

/** Variant fields — differences here make products distinct alternatives */
const VARIANT_FIELDS = ['carbideGrade', 'buttonShape', 'faceDesign', 'skirtDesign', 'bitProductType'];

/**
 * Find similar products based on shared dimensions with variant differences.
 * Works for TH and DTH bit categories only.
 *
 * @param {Object} product - The current product
 * @param {Array} allProducts - All products from cache
 * @param {Object} [options] - { maxResults: 6, productLine: string }
 * @returns {Array} Similar products with _variantDiffs annotation
 */
export function findSimilarProducts(product, allProducts, options = {}) {
  const { maxResults = 6 } = options;

  // Only compute for bit categories
  if (!BIT_CATEGORIES.includes(product.subcategory)) return [];

  // Filter candidates: same core fields, same product line, different item
  const candidates = allProducts.filter((p) => {
    if (p.itemCode === product.itemCode) return false;
    if (p.productLine !== product.productLine) return false;
    return CORE_FIELDS.every((f) => p[f] === product[f]);
  });

  // Score each candidate
  const scored = candidates.map((p) => {
    const dimMatches = DIMENSION_FIELDS.filter(
      (f) => product[f] && p[f] && String(product[f]) === String(p[f])
    ).length;

    const variantDiffs = VARIANT_FIELDS.filter(
      (f) => product[f] && p[f] && String(product[f]) !== String(p[f])
    );

    const hasVariantDiff = variantDiffs.length > 0;

    return {
      product: p,
      score: dimMatches * 10 + (hasVariantDiff ? 5 : 0),
      variantDiffs: variantDiffs.map((f) => ({
        field: f,
        currentValue: product[f],
        altValue: p[f],
      })),
    };
  });

  // Must share at least 1 dimension AND have at least 1 variant difference (score >= 15)
  return scored
    .filter((s) => s.score >= 15)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map((s) => ({ ...s.product, _variantDiffs: s.variantDiffs }));
}

/**
 * Find premium alternative product.
 *
 * @param {Object} product - Current product (may have premiumAlternative field)
 * @param {Array} allProducts - All products
 * @returns {Object|null} The premium alternative product, or null
 */
export function getPremiumAlternative(product, allProducts) {
  if (!product.premiumAlternative) return null;
  return allProducts.find((p) => p.itemCode === product.premiumAlternative) || null;
}

/**
 * Cross-category related products — inferred from data (no manual mapping).
 * Returns `[]` when no relation applies so the PDP can hide the section.
 *
 * Currently wired:
 *   - services-resharpening-machines → grinding cups whose
 *     compatibleMachines[] contains this machine's itemCode
 *   - services-resharpening-cups → grinding machines listed in this cup's
 *     compatibleMachines[]
 *
 *   - shank-adapters → other shanks fitting the same rock drill
 *     (compatibleRockDrill match, 96% populated in TH data). Falls back
 *     to same-diameterMm / different-thread when the primary match is thin.
 *
 * Extension points (future):
 *   - shank-adapters → related rods / couplings / bits with a matching thread
 *   - Any product with a `related[]` list on the record itself
 *
 * @param {Object} product
 * @param {Array<Object>} allProducts
 * @returns {Array<Object>}
 */
export function findCompatibleProducts(product, allProducts) {
  if (!product?.subcategory || !Array.isArray(allProducts)) return [];

  // Machine PDP → cups that reference this machine
  if (product.subcategory === 'services-resharpening-machines') {
    return allProducts
      .filter((p) => p.subcategory === 'services-resharpening-cups'
        && Array.isArray(p.compatibleMachines)
        && p.compatibleMachines.includes(product.itemCode))
      .slice(0, 24);
  }

  // Cup PDP → machines this cup fits (drop legacy stubs so we lead with
  // sellable current variants; if fewer than 2 current found, fall back
  // to include legacies so the section still surfaces something).
  if (product.subcategory === 'services-resharpening-cups') {
    const codes = Array.isArray(product.compatibleMachines) ? product.compatibleMachines : [];
    if (!codes.length) return [];
    const matches = allProducts.filter((p) =>
      p.subcategory === 'services-resharpening-machines' && codes.includes(p.itemCode));
    const current = matches.filter((m) => !m.isLegacy);
    if (current.length >= 2) return current;
    return matches;
  }

  // TH shank adapter PDP → other shanks that fit the same rock drill.
  // Primary signal: compatibleRockDrill (96% populated in TH catalog).
  // Fallback: same diameterMm, different thread — useful when the primary
  // match is thin (this covers "swap the drill string" cases).
  if (product.subcategory === 'shank-adapters') {
    const sameShank = (p) =>
      p.itemCode !== product.itemCode && p.subcategory === 'shank-adapters';
    let matches = [];
    if (product.compatibleRockDrill) {
      matches = allProducts.filter((p) =>
        sameShank(p) && p.compatibleRockDrill === product.compatibleRockDrill);
    }
    if (matches.length < 2 && product.diameterMm) {
      const extras = allProducts.filter((p) =>
        sameShank(p)
        && p.diameterMm === product.diameterMm
        && p.thread && product.thread && p.thread !== product.thread
        && !matches.some((m) => m.itemCode === p.itemCode));
      matches = matches.concat(extras);
    }
    return matches.slice(0, 12);
  }

  return [];
}
